import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';
import { generatePath } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { AveragePlayerScore } from '@sorare/core/src/__generated__/globalTypes';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { HeadlineS } from '@sorare/core/src/atoms/typography';
import {
  MainAsideLayout,
  Section,
} from '@sorare/core/src/components/layout/MainAsideLayout';
import { NewsTitleContent } from '@sorare/core/src/components/player/News/NewsTitleContent';
import { NewsUpdates } from '@sorare/core/src/components/player/News/NewsUpdates';
import { PlayerHandsInfo } from '@sorare/core/src/components/player/PlayerHandsInfo';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { Header } from '@sorare/core/src/components/seo/Header';
import { MetricProps } from '@sorare/core/src/components/seo/HeaderMetrics';
import { CardSupply } from '@sorare/core/src/components/seo/Player/CardSupply';
import { Infos } from '@sorare/core/src/components/seo/Player/Infos';
import { Country } from '@sorare/core/src/components/seo/Player/Infos/Country';
import { MLB_TEAMS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { isType } from '@sorare/core/src/gql';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import {
  battingThrowingHandMessage,
  getPlayerInjuryColor,
  getPlayerInjuryMessage,
} from '@sorare/core/src/lib/baseball';
import {
  cardAttributes,
  filters,
  glossary,
} from '@sorare/core/src/lib/glossary';

import { PriceGraph } from '@sorare/marketplace/src/components/price/PriceGraph';

import { GameWeekProjection } from '@sorare/gameplay/src/components/GameWeekProjection';
import { LazyFixtureChart } from '@sorare/gameplay/src/components/stats/LastScores/LazyFixtureChart';

import { GamesNavigator } from './Overview/GamesNavigator';
import {
  BaseballPlayerOverviewQuery,
  BaseballPlayerOverviewQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const messages = defineMessages({
  injuryStatus: {
    id: 'MLB.Seo.Players.Slug.Overview.injuryStatus',
    defaultMessage: 'Injury Status',
  },
  injuryReturn: {
    id: 'MLB.Seo.Players.Slug.Overview.injuryMessage',
    defaultMessage: 'Est. Return {returnDate} ({description})',
  },
});

const BASEBALL_PLAYER_OVERVIEW_QUERY = gql`
  query BaseballPlayerOverviewQuery($slug: String!) {
    anyPlayer(slug: $slug) {
      slug
      squaredPictureUrl
      displayName
      age
      shirtNumber
      cardSupply {
        season {
          startYear
        }
      }
      activeClub {
        id
        slug
        name
        pictureUrl
      }
      country {
        slug
        code
        threeLetterCode
        name
      }
      ...CardSupply_anyPlayerInterface
      ...PlayerScore_anyPlayer
      ...PlayerHandsInfo_AnyPlayerInterface
      ... on BaseballPlayer {
        slug
        # eslint-disable-next-line @graphql-eslint/no-deprecated
        playerInjury {
          status
          description
          comment
          startDate
          returnDate
          updateDateTime
        }
        ...GameWeekProjection_AnyPlayerInterface
        ...Header_subscription
      }
      news(last: 10) {
        ...NewsUpdates_PlayerNewsUpdate
      }
    }
  }
  ${NewsUpdates.fragments.PlayerNewsUpdate}
  ${CardSupply.fragments.anyPlayer}
  ${Header.fragments.subscription}
  ${PlayerScore.fragments.anyPlayer}
  ${PlayerHandsInfo.fragments.AnyPlayerInterface}
  ${GameWeekProjection.fragments.anyPlayerInterface}
` as TypedDocumentNode<
  BaseballPlayerOverviewQuery,
  BaseballPlayerOverviewQueryVariables
>;

const Overview = () => {
  const {
    flags: { useDailyStatProjection = false },
  } = useFeatureFlags();
  const { slug } = useTypedParams<PageParams>();
  const { formatDate } = useIntlContext();
  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const { data, loading } = useQuery(BASEBALL_PLAYER_OVERVIEW_QUERY, {
    variables: { slug },
    skip: !slug,
  });

  if (loading) return <LoadingIndicator fullHeight />;
  const baseballPlayer = data?.anyPlayer;
  if (!isType(baseballPlayer, 'BaseballPlayer')) return null;

  let injury: MetricProps | undefined;
  if (baseballPlayer.playerInjury) {
    const { status, description, returnDate } = baseballPlayer.playerInjury;

    injury = {
      label: <FormattedMessage {...messages.injuryStatus} />,
      color: `var(--c-${getPlayerInjuryColor(status)}-600)`,
      body: (
        <FormattedMessage {...getPlayerInjuryMessage(status, { long: true })} />
      ),
      subLabel: returnDate ? (
        <FormattedMessage
          {...messages.injuryReturn}
          values={{
            description,
            returnDate: formatDate(returnDate),
          }}
        />
      ) : (
        description
      ),
    };
  }

  const blocks = {
    header: (
      <Header
        displayName={baseballPlayer.displayName}
        pictureUrl={baseballPlayer.squaredPictureUrl}
        memberships={[
          ...(baseballPlayer.activeClub
            ? [
                {
                  name: baseballPlayer.activeClub.name,
                  pictureUrl: baseballPlayer.activeClub.pictureUrl,
                  to: generatePath(MLB_TEAMS_SLUG, {
                    slug: baseballPlayer.activeClub.slug,
                  }),
                },
              ]
            : []),
        ]}
        scores={[
          {
            label: <FormattedMessage {...cardAttributes.last15} />,
            body: (
              <PlayerScore
                mode={AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE}
                player={baseballPlayer}
              />
            ),
          },
          {
            label: <FormattedMessage {...battingThrowingHandMessage.header} />,
            body: <PlayerHandsInfo player={baseballPlayer} />,
          },
          useDailyStatProjection && {
            label: <FormattedMessage {...glossary.projectionShort} />,
            body: <GameWeekProjection player={baseballPlayer} />,
          },
        ].filter(Boolean)}
        subscription={baseballPlayer}
        injury={injury}
      />
    ),
    infos: (
      <Infos
        informations={[
          {
            label: cardAttributes.jerseyNumber,
            value: <>#{baseballPlayer.shirtNumber}</>,
          },
          {
            label: cardAttributes.age,
            value: baseballPlayer.age,
          },
          {
            label: filters.birthplace,
            value: (
              <Country
                code={baseballPlayer.country.code}
                threeLetterCode={baseballPlayer.country.threeLetterCode}
              />
            ),
          },
        ].filter(Boolean)}
      />
    ),
    news: (
      <FullWidthContainer>
        <Vertical gap={1.5}>
          <HeadlineS as="h2">
            <NewsTitleContent />
          </HeadlineS>
          <NewsUpdates
            updates={baseballPlayer.news}
            playerName={baseballPlayer.displayName}
          />
        </Vertical>
      </FullWidthContainer>
    ),
    performance: (
      <FullWidthContainer>
        <LazyFixtureChart
          TitleComponent={HeadlineS}
          playerSlug={baseballPlayer.slug}
        />
      </FullWidthContainer>
    ),
    fixtures: <GamesNavigator playerSlug={baseballPlayer.slug} />,
    cardsSupply: <CardSupply anyPlayer={baseballPlayer} />,
    priceGraph: <PriceGraph playerSlug={baseballPlayer.slug} />,
  };
  if (isLaptopAndAbove) {
    return (
      <MainAsideLayout
        header={blocks.header}
        aside={[
          blocks.infos,
          ...(baseballPlayer.cardSupply?.length ? [blocks.cardsSupply] : []),
          blocks.news,
        ]}
      >
        {blocks.performance}
        {blocks.priceGraph}
        {blocks.fixtures}
      </MainAsideLayout>
    );
  }

  return (
    <Section>
      {blocks.header}
      {blocks.infos}
      {blocks.cardsSupply}
      {blocks.news}
      {blocks.performance}
      {blocks.priceGraph}
      {blocks.fixtures}
    </Section>
  );
};

export default Overview;
